import { IconButton, useMediaQuery } from '@material-ui/core';
import { CircularProgress, Paper } from '@mui/material';
import { Theme } from '@mui/system';
import React, {
  useEffect, useMemo, useState,
} from 'react';
import Masonry from '@mui/lab/Masonry';
import { getImage, getImageData } from '../../../api/Image';
import { DialogCloseImg } from '../../../assets/icons';
import { useEditor } from '../../../hooks/useEditor';
import { debounce } from '../../../utils';
import SearchInput from '../../inputs/SearchInput';
import { Description } from '../../typography';
import { UploadDialogProps } from './types';
import { UploadDialogContainer, UploadDialogTitle, UploadDialogContent } from './styles';
import ImageElement from './ImageElement';
import { useDocumentStore } from '../../../zustand/documents';
import * as ImageAPI from '../../../api/Image';
import { lang } from '../../../lang';

function UploadDialog({ currentElement, isOpen, onClose }: UploadDialogProps) {
  const mobileMQ = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const tabletMQ = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const currentDocument = useDocumentStore((state) => state.currentDocument);

  const {
    handleElChange, setIsBusy,
  } = useEditor();

  const [searchValue, setSearchValue] = useState('');
  const [imagesArray, setImagesArray] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const loadPageData = async () => {
    if (!currentDocument?.id && !currentDocument?.isTryIt) return;
    const { data } = await getImage(currentDocument?.id || 'try-it', {
      text: searchValue,
      page: currentPage,
    });
    if (data?.data) {
      setImagesArray((prev) => prev
        .concat(data?.data)
        .filter((v, i, a) => a.findIndex((v2) => v2.provider_id === v.provider_id) === i));
      setCurrentPage((prev) => prev + 1);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isProcessing) return;
      if (imagesArray.length === 0 || isLoading) loadPageData();
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    debounce(() => {
      setIsLoading(true);
      setCurrentPage(1);
      setImagesArray([]);
    }, 1000);
  }, [searchValue]);

  const handleClose = () => {
    setSearchValue('');
    setIsBusy(false);
    setIsProcessing(false);

    if (searchValue) {
      setImagesArray([]);
    }

    onClose();
  };

  const readFile = async (file: File) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });

  const handleSubmit = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !currentElement) return;
    const file = event.target.files[0];
    if (!currentDocument) return;

    if (currentDocument?.isTryIt) {
      const imageDataUrl = await readFile(file);

      handleElChange(currentElement, 'originalImage', imageDataUrl);
      handleClose();
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await ImageAPI.upload(
        currentDocument.id,
        formData,
      );

      handleElChange(currentElement, 'originalImage', response.data.data.url);
      handleClose();
    } catch (err: any) {
      setError(err.response.data.message);
    }
  };

  const handleImageChosen = async (image: string) => {
    if (!currentElement || !currentDocument) return;
    setIsProcessing(true);
    try {
      const { data } = await getImageData(currentDocument?.id || 'try-it', { image_id: image });
      if (data.data) {
        handleElChange(currentElement, 'originalImage', data.data.url);
        handleClose();
      } else {
        setIsProcessing(false);
      }
    } catch {
      setIsProcessing(false);
    }
  };

  const handleScroll = (e: any) => {
    const section = e.target.firstChild;
    const scrollHeight = e.target.clientHeight + e.target.scrollTop;
    if (isLoading || scrollHeight < section.clientHeight * 0.9) return;
    setIsLoading(true);
  };

  const columns = useMemo(() => {
    if (mobileMQ) return 2;
    if (tabletMQ) return 3;
    return 4;
  }, [mobileMQ, tabletMQ]);

  return (
    <UploadDialogContainer open={isOpen} onClose={handleClose}>
      <UploadDialogTitle>
        <Description size="small">
          {lang('document.upload_dialog.title.line_1')}
        </Description>
        <Description>{lang('document.upload_dialog.title.line_2')}</Description>
      </UploadDialogTitle>
      <UploadDialogContent>
        <div className="search-bar">
          <div>
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder={lang('document.upload_dialog.title.search_placeholder')}
              disabled={isProcessing}
            />
          </div>
          <div className="sub-bar">
            {lang('document.upload_dialog.upload_message.line_1')}
            {' '}
            <label
              id="upload-button"
              style={{ cursor: 'pointer' }}
              htmlFor="upload-file-input"
            >
              {lang('document.upload_dialog.upload_message.line_2')}
              <input
                id="upload-file-input"
                type="file"
                onChange={handleSubmit}
                hidden
                accept="image/*"
                disabled={isProcessing}
              />
            </label>
            <br />
            {error && <Description size="small" style={{ color: 'red' }}>{error}</Description>}
          </div>
        </div>
        <div className="image-grid" onScroll={handleScroll} style={isProcessing ? { opacity: 0.5 } : {}}>
          <Masonry columns={columns}>
            {imagesArray.map((el) => (
              <Paper style={{ boxShadow: 'none' }} className="image-container" key={el.provider_id}>
                <ImageElement
                  isProcessing={isProcessing}
                  element={el}
                  onClick={() => { handleImageChosen(el.provider_id); }}
                />
              </Paper>
            ))}
          </Masonry>
          {isLoading && (
            <div className="loading-indicator" style={{ color: '#999999' }}>
              <CircularProgress color="inherit" />
            </div>
          )}
        </div>
      </UploadDialogContent>

      <div className="dialog-footer" />

      <IconButton className="close-button" onClick={handleClose}>
        <img src={DialogCloseImg} alt="close dialog" />
      </IconButton>
    </UploadDialogContainer>
  );
}

export default UploadDialog;
